import { ShieldCheck, EyeOff, Mail, Lock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// Privacy Score badge — surfaces the three genuine differentiators of StardomFans.com
// vs. big-social platforms: zero third-party trackers, no OAuth/data-broker sign-in,
// and no outbound marketing email. Rendered on Splash + Landing hero surfaces.
// Clicking either variant takes visitors to /privacy-report for the full audit.
//
// Two visual variants:
//   variant="compact"  — pill row, ideal directly under a CTA
//   variant="hero"     — full card with score readout, ideal below hero copy
const items = [
  { icon: EyeOff, label: "0 Trackers", detail: "No PostHog, GA, Meta, TikTok pixels" },
  { icon: Lock,   label: "No 3rd-Party Sign-In", detail: "Your account never touches Google or Meta" },
  { icon: Mail,   label: "No Outbound Email", detail: "Everything routes through your in-app inbox" },
];

const PrivacyScoreBadge = ({ variant = "hero", className = "" }) => {
  if (variant === "compact") {
    return (
      <Link
        to="/privacy-report"
        className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3.5 py-2 rounded-full border border-[#00E68A]/30 bg-[#00E68A]/[0.06] backdrop-blur-md hover:border-[#00E68A]/60 hover:bg-[#00E68A]/[0.10] transition-colors group ${className}`}
        data-testid="privacy-score-compact"
        aria-label="StardomFans privacy score: 100 out of 100 — read the full privacy report"
      >
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-[#00E68A]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#00E68A]">
            Privacy Score 100/100
          </span>
        </div>
        <span className="hidden sm:inline w-px h-3 bg-[#00E68A]/30" aria-hidden="true" />
        <span className="text-[10px] text-[#8A92A0] font-medium">
          0 trackers · No 3rd-party sign-in · No outbound email
        </span>
        <ArrowUpRight className="w-3 h-3 text-[#00E68A] opacity-70 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <Link
      to="/privacy-report"
      className={`relative block rounded-2xl border border-[#00E68A]/25 bg-gradient-to-br from-[#00E68A]/[0.06] via-[#0A0E18]/60 to-transparent backdrop-blur-md overflow-hidden hover:border-[#00E68A]/50 transition-colors group ${className}`}
      data-testid="privacy-score-badge"
      aria-label="StardomFans privacy score: 100 out of 100 — read the full privacy report"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#00E68A]/10 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#00E68A]/15 border border-[#00E68A]/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#00E68A]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-['Unbounded'] text-2xl font-black text-[#00E68A] leading-none">100</span>
              <span className="text-[#8A92A0] text-sm">/ 100</span>
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-widest text-[#00E68A] ml-1 px-2 py-0.5 rounded-full bg-[#00E68A]/10 border border-[#00E68A]/25">
                Privacy Score
              </span>
            </div>
            <p className="text-xs text-[#8A92A0] mt-1">A creator-first internet, verified by our own audit.</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-[#00E68A] opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" aria-hidden="true" />
        </div>

        <ul className="grid gap-2.5 sm:gap-2" role="list">
          {items.map(({ icon: Icon, label, detail }) => (
            <li key={label} className="flex items-start gap-2.5">
              <Icon className="w-4 h-4 text-[#00E68A] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold text-white leading-tight">{label}</p>
                <p className="text-[11px] text-[#8A92A0] leading-tight">{detail}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-[10px] text-[#00E68A] font-semibold uppercase tracking-widest mt-4 pt-3 border-t border-[#1A2030] group-hover:text-white transition-colors">
          Read the full audit →
        </p>
      </div>
    </Link>
  );
};

export default PrivacyScoreBadge;
